'use strict';

import { WebAppState } from '../enums/WebAppState';

type BatteryStatus = EventTarget & { level: number };

export class CallerWatcher {
    incidentCode!: string;
    watchPositionId: number | null = null;
    private lestServerPosition!: GeolocationPosition;
    private batteryLevel!: number;
    private stopWatching: (() => void) | null = null;
    private battery: BatteryStatus | null = null;
    private stopped = false;
    private readonly updateBatteryLevel = () => {
        this.batteryLevel = this.battery!.level * 100;
    };

    constructor(private readonly reportState: (state: WebAppState) => Promise<unknown>) {
    }

    public async watch(incidentCode: string) {
        this.incidentCode = incidentCode;

        this.getBatteryLevel();

        if (!navigator.geolocation) {
            await this.reportState(WebAppState.LocationUnavailableNotSupported);
            return;
        }

        let permission: PermissionStatus | null = null;
        try {
            permission = await navigator.permissions?.query({ name: 'geolocation' }) ?? null;
        } catch {
        }

        if (this.stopped) return;
        if (permission === null || permission.state === 'prompt') {
            await this.reportState(WebAppState.LocationPermissionRequested);
        }
        if (this.stopped) return;

        await new Promise<void>((resolve, reject) => {
            let resolved = false;
            const finishPermission = (state: WebAppState) => {
                if (resolved) return;
                resolved = true;
                permission?.removeEventListener('change', onPermissionChanged);
                void this.reportState(state).then(() => resolve(), reject);
            };
            const onPermissionChanged = () => {
                if (permission?.state === 'granted') finishPermission(WebAppState.LocationPermissionGranted);
                if (permission?.state === 'denied') finishPermission(WebAppState.LocationPermissionDenied);
            };

            permission?.addEventListener('change', onPermissionChanged);
            this.stopWatching = () => {
                resolved = true;
                permission?.removeEventListener('change', onPermissionChanged);
                if (this.watchPositionId !== null) navigator.geolocation.clearWatch(this.watchPositionId);
                this.watchPositionId = null;
                resolve();
            };
            this.watchPositionId = navigator.geolocation.watchPosition(
                position => {
                    this.onPositionChange(position);
                    finishPermission(WebAppState.LocationPermissionGranted);
                },
                error => {
                    finishPermission(error.code === 1 ? WebAppState.LocationPermissionDenied
                        : error.code === 3 ? WebAppState.LocationTimeout : WebAppState.LocationPositionUnavailable);
                },
                { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 },
            );
            onPermissionChanged();
        });
    }

    public stop() {
        this.stopped = true;
        this.stopWatching?.();
        this.stopWatching = null;
        this.battery?.removeEventListener('levelchange', this.updateBatteryLevel);
        this.battery = null;
    }

    protected getBatteryLevel() {
        const batteryNavigator = navigator as Navigator & {
            battery?: BatteryStatus;
            webkitBattery?: BatteryStatus;
            mozBattery?: BatteryStatus;
            msBattery?: BatteryStatus;
            getBattery?: () => Promise<BatteryStatus>;
        };
        const battery = batteryNavigator.battery || batteryNavigator.webkitBattery || batteryNavigator.mozBattery || batteryNavigator.msBattery;
        if (battery) {
            this.setBatteryLevel(battery);
        } else if (batteryNavigator.getBattery) {
            void batteryNavigator.getBattery().then(battery => this.setBatteryLevel(battery));
        }
    }

    protected setBatteryLevel(battery: BatteryStatus) {
        if (this.stopped) return;
        this.battery = battery;
        this.updateBatteryLevel();
        battery.addEventListener('levelchange', this.updateBatteryLevel);
    }

    protected onPositionChange(newPosition: GeolocationPosition) {
        if (this.shouldUpdateOrReplacePosition(newPosition)) {
            this.updatePosition(newPosition);
        }
    }

    private updatePosition(newPosition: GeolocationPosition) {
        this.lestServerPosition = newPosition;
        const coords = this.lestServerPosition.coords;
        const body = `${coords.latitude};${coords.longitude};${coords.altitude};${coords.accuracy};${newPosition.timestamp};${this.batteryLevel}`;
        this.sendPositionUpdateRequest(body);
    }

    private sendPositionUpdateRequest(body: string) {
        const http = new XMLHttpRequest();
        http.open("POST", "/u", true);
        http.send(this.incidentCode + ";" + body);
    }

    private shouldUpdateOrReplacePosition(newPosition: GeolocationPosition): boolean {
        const goodEnoughAccuracy = 100;

        const isNewer = (): boolean => {
            return this.lestServerPosition.timestamp <= newPosition.timestamp;
        }

        const isInTimeTolerance = (): boolean => {
            var timeTolerance = 5 * 60 * 1000; // 5 seconds
            return newPosition.timestamp + timeTolerance > Date.now();
        }

        const isMoreAccurateOrFarEnough = (): boolean => {
            const isFarEnough = (): boolean => {
                return newPosition.coords.accuracy <= goodEnoughAccuracy && this.distance(this.lestServerPosition.coords, newPosition.coords) > requestedAccuracy;
            }

            var requestedAccuracy = 50;
            if (this.lestServerPosition.coords.accuracy < requestedAccuracy) {
                return isFarEnough();
            }

            return this.lestServerPosition.coords.accuracy > newPosition.coords.accuracy;
        }


        if (!this.lestServerPosition) {
            return true;
        }

        return isInTimeTolerance() && isNewer() && isMoreAccurateOrFarEnough();
    }

    private distance(coordinatesA: GeolocationCoordinates, coordinatesB: GeolocationCoordinates): number {
        if ((coordinatesA.latitude === coordinatesB.latitude) && (coordinatesA.longitude === coordinatesB.longitude)) {
            return 0;
        }
        else {
            const radlat1 = Math.PI * coordinatesA.latitude / 180;
            const radlat2 = Math.PI * coordinatesB.latitude / 180;
            const theta = coordinatesA.longitude - coordinatesB.longitude;
            const radtheta = Math.PI * theta / 180;
            let dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
            if (dist > 1) {
                dist = 1;
            }
            dist = Math.acos(dist);
            dist = dist * 180 / Math.PI;
            dist = dist * 60 * 1.1515;
            dist = dist * 1.609344 * 1000;
            return dist;
        }
    }
}
